import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { AlertTriangle, Ban, Loader2, RefreshCw, Mail, Users, Euro } from 'lucide-react';
import { toast } from 'sonner';
import { courseCancellationApi } from '../../lib/api';

const REASON_TEMPLATES = [
    'Zu wenige Anmeldungen — Mindestteilnehmerzahl nicht erreicht.',
    'Krankheit des Referenten — Ersatz kurzfristig nicht verfügbar.',
    'Höhere Gewalt (Wetter, Infrastruktur).',
    'Räumlichkeit kurzfristig nicht verfügbar.',
];

const ACTION_LABELS = {
    offer_choice: 'Wahl per E-Mail anbieten',
    auto_rebook: 'Direkt umbuchen',
    refund_only: 'Nur Erstattung',
};

const eur = (n) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

/**
 * Kurs-Absage-Dialog. Der Admin wählt einen Grund, sieht alle Buchungen mit
 * bereits gezahltem Betrag und legt pro Teilnehmer fest, wie verfahren werden
 * soll: Wahl anbieten, direkt umbuchen (mit Ziel-Termin) oder nur erstatten.
 */
export function CancelCourseDialog({ open, onOpenChange, seminarId, onCancelled }) {
    const [preview, setPreview] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [reason, setReason] = useState('');
    const [defaultAction, setDefaultAction] = useState('offer_choice');
    const [defaultTarget, setDefaultTarget] = useState('');
    const [perParticipant, setPerParticipant] = useState({}); // {booking_id: {action, target_seminar_id}}
    const [notifyReferents, setNotifyReferents] = useState(true);

    useEffect(() => {
        if (!open || !seminarId) return;
        setLoading(true);
        setPerParticipant({});
        Promise.all([
            courseCancellationApi.getPreview(seminarId),
            courseCancellationApi.getRebookCandidates(seminarId),
        ])
            .then(([p, c]) => {
                setPreview(p.data);
                setCandidates(c.data || []);
                if ((c.data || []).length > 0) setDefaultTarget(c.data[0].id);
            })
            .catch((e) => toast.error(e.response?.data?.detail || 'Vorschau konnte nicht geladen werden'))
            .finally(() => setLoading(false));
    }, [open, seminarId]);

    const hasCandidates = candidates.length > 0;

    const setPP = (bookingId, patch) => {
        setPerParticipant((prev) => ({
            ...prev,
            [bookingId]: { ...(prev[bookingId] || {}), ...patch },
        }));
    };

    const submit = async () => {
        if (reason.trim().length < 3) {
            toast.error('Bitte einen Absagegrund angeben');
            return;
        }
        if (defaultAction === 'auto_rebook' && !defaultTarget) {
            toast.error('Bitte einen Ziel-Termin für die automatische Umbuchung wählen');
            return;
        }
        const per = Object.entries(perParticipant)
            .filter(([, v]) => v && v.action)
            .map(([booking_id, v]) => ({
                booking_id,
                action: v.action,
                target_seminar_id: v.action === 'auto_rebook' ? (v.target_seminar_id || defaultTarget) : null,
            }));
        // Validierung: kein auto_rebook ohne target
        for (const p of per) {
            if (p.action === 'auto_rebook' && !p.target_seminar_id) {
                toast.error(`Bitte Ziel-Termin für Buchung ${p.booking_id.slice(0, 6)} wählen`);
                return;
            }
        }
        setSubmitting(true);
        try {
            const res = await courseCancellationApi.cancelCourse(seminarId, {
                reason: reason.trim(),
                default_action: defaultAction,
                default_target_seminar_id: defaultAction === 'auto_rebook' ? defaultTarget : null,
                per_participant: per,
                notify_referents: notifyReferents,
            });
            const s = res.data.stats || {};
            toast.success(
                `Kurs abgesagt. ${s.auto_rebooked || 0} umgebucht, ${s.offered_choice || 0} zur Wahl, ${s.refund_only || 0} nur Erstattung · ${s.mails_sent || 0} E-Mails versendet.`
            );
            onCancelled && onCancelled();
            onOpenChange(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Absage fehlgeschlagen');
        } finally {
            setSubmitting(false);
        }
    };

    const affected = preview?.bookings || [];
    const paidTotal = preview?.totals?.paid ?? 0;
    const openTotal = preview?.totals?.open ?? 0;

    const candidateLabel = (c) => `${c.course_number || '—'} · ${c.date || '—'} · ${c.free_seats}/${c.max_participants} frei`;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="cancel-course-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-800">
                        <Ban className="h-5 w-5" />
                        Kurs absagen
                    </DialogTitle>
                    <DialogDescription>
                        Der Kurs wird abgesagt, alle Buchungen storniert. Für jeden Teilnehmer legen Sie fest,
                        wie verfahren werden soll: E-Mail mit Wahl, sofortige Umbuchung oder reine Erstattung.
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="py-12 flex justify-center">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                ) : preview ? (
                    <div className="space-y-6">
                        {/* Übersicht */}
                        <div className="grid grid-cols-3 gap-3">
                            <div className="border rounded-lg p-3 bg-muted/20">
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-0.5">
                                    <Users className="h-3.5 w-3.5" /> Betroffen
                                </div>
                                <div className="text-xl font-bold" data-testid="cancel-preview-count">{preview.totals.count}</div>
                            </div>
                            <div className="border rounded-lg p-3 bg-emerald-50">
                                <div className="flex items-center gap-1.5 text-xs text-emerald-900 mb-0.5">
                                    <Euro className="h-3.5 w-3.5" /> Bereits gezahlt
                                </div>
                                <div className="text-xl font-bold text-emerald-900" data-testid="cancel-preview-paid">{eur(paidTotal)}</div>
                            </div>
                            <div className="border rounded-lg p-3 bg-amber-50">
                                <div className="flex items-center gap-1.5 text-xs text-amber-900 mb-0.5">
                                    <Euro className="h-3.5 w-3.5" /> Offen
                                </div>
                                <div className="text-xl font-bold text-amber-900" data-testid="cancel-preview-open">{eur(openTotal)}</div>
                            </div>
                        </div>

                        {/* Absagegrund */}
                        <div className="space-y-2">
                            <Label htmlFor="cancel-reason">Absagegrund *</Label>
                            <Textarea
                                id="cancel-reason"
                                rows={3}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Bitte kurz erläutern — Text wird in die E-Mail an die Teilnehmer übernommen."
                                maxLength={2000}
                                data-testid="cancel-reason-input"
                            />
                            <div className="flex flex-wrap gap-1.5">
                                {REASON_TEMPLATES.map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        onClick={() => setReason(t)}
                                        className="text-xs px-2 py-1 rounded-md border bg-muted/40 hover:bg-muted transition-colors"
                                        data-testid="cancel-reason-preset"
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Default-Aktion */}
                        <div className="space-y-3 border rounded-lg p-3 bg-muted/10">
                            <Label className="text-sm font-semibold">Standard-Verfahren für alle Teilnehmer</Label>
                            <RadioGroup value={defaultAction} onValueChange={setDefaultAction} className="space-y-2" data-testid="default-action-radio">
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <RadioGroupItem value="offer_choice" id="da-choice" className="mt-0.5" />
                                    <div className="text-sm">
                                        <div className="font-medium">Wahl per E-Mail anbieten (empfohlen)</div>
                                        <div className="text-xs text-muted-foreground">Teilnehmer entscheiden über personalisierten Link: Umbuchung oder Erstattung.</div>
                                    </div>
                                </label>
                                <label className={`flex items-start gap-2 ${hasCandidates ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
                                    <RadioGroupItem value="auto_rebook" id="da-rebook" className="mt-0.5" disabled={!hasCandidates} />
                                    <div className="text-sm">
                                        <div className="font-medium">Direkt umbuchen</div>
                                        <div className="text-xs text-muted-foreground">Alle Teilnehmer werden sofort auf den gewählten Ersatz-Termin gebucht (Zahlung wandert mit).</div>
                                    </div>
                                </label>
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <RadioGroupItem value="refund_only" id="da-refund" className="mt-0.5" />
                                    <div className="text-sm">
                                        <div className="font-medium">Nur Erstattung</div>
                                        <div className="text-xs text-muted-foreground">Keine Umbuchung anbieten — gezahlte Beträge landen in „Zur Rückerstattung".</div>
                                    </div>
                                </label>
                            </RadioGroup>

                            {defaultAction === 'auto_rebook' && (
                                <div className="pt-2">
                                    <Label htmlFor="default-target" className="text-xs">Ziel-Termin</Label>
                                    <select
                                        id="default-target"
                                        value={defaultTarget}
                                        onChange={(e) => setDefaultTarget(e.target.value)}
                                        className="w-full mt-1 border rounded-md px-2 py-1.5 text-sm bg-background"
                                        data-testid="default-target-select"
                                    >
                                        {candidates.map((c) => (
                                            <option key={c.id} value={c.id}>{candidateLabel(c)}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {!hasCandidates && (
                                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
                                    Für diesen Kurs sind aktuell keine passenden Ersatz-Termine im System.
                                </div>
                            )}
                        </div>

                        {/* Per-Teilnehmer Overrides */}
                        {affected.length > 0 && (
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold">Individuelle Anpassung (optional)</Label>
                                <div className="border rounded-lg divide-y max-h-72 overflow-y-auto">
                                    {affected.map((b) => {
                                        const pp = perParticipant[b.booking_id];
                                        const effective = pp?.action || defaultAction;
                                        const effectiveTarget = pp?.target_seminar_id || defaultTarget;
                                        return (
                                            <div key={b.booking_id} className="p-2.5 text-sm flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between" data-testid={`cancel-row-${b.booking_id}`}>
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-medium truncate">
                                                        {b.participant_name || '—'}
                                                        {b.is_referent && <span className="ml-2 text-xs text-blue-700">(Referent)</span>}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground truncate">
                                                        {b.participant_email} · gezahlt: {eur(b.paid_amount)} · offen: {eur(b.open_amount)}
                                                    </div>
                                                </div>
                                                {!b.is_referent && (
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <select
                                                            value={effective}
                                                            onChange={(e) => setPP(b.booking_id, { action: e.target.value })}
                                                            className="border rounded-md px-2 py-1 text-xs bg-background"
                                                            data-testid={`cancel-action-${b.booking_id}`}
                                                        >
                                                            <option value="offer_choice">{ACTION_LABELS.offer_choice}</option>
                                                            <option value="auto_rebook" disabled={!hasCandidates}>{ACTION_LABELS.auto_rebook}</option>
                                                            <option value="refund_only">{ACTION_LABELS.refund_only}</option>
                                                        </select>
                                                        {effective === 'auto_rebook' && (
                                                            <select
                                                                value={effectiveTarget}
                                                                onChange={(e) => setPP(b.booking_id, { target_seminar_id: e.target.value })}
                                                                className="border rounded-md px-2 py-1 text-xs bg-background"
                                                                data-testid={`cancel-target-${b.booking_id}`}
                                                            >
                                                                {candidates.map((c) => (
                                                                    <option key={c.id} value={c.id}>{candidateLabel(c)}</option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Referenten-Toggle */}
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox
                                checked={notifyReferents}
                                onCheckedChange={(v) => setNotifyReferents(!!v)}
                                data-testid="notify-referents-checkbox"
                            />
                            <span className="flex items-center gap-1.5">
                                <Mail className="h-3.5 w-3.5" />
                                Referenten benachrichtigen
                            </span>
                        </label>

                        <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-md p-2 flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                            <span>
                                Diese Aktion ist nicht direkt reversibel. Bereits gezahlte Beträge werden bei „Nur Erstattung" und
                                bei „Wahl anbieten" mit Erstattungs-Wahl in „Zur Rückerstattung" markiert.
                            </span>
                        </div>
                    </div>
                ) : null}

                <DialogFooter className="gap-2 sm:gap-0">
                    <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>Abbrechen</Button>
                    <Button
                        variant="destructive"
                        onClick={submit}
                        disabled={submitting || loading || !preview}
                        data-testid="cancel-course-submit"
                    >
                        {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Ban className="h-4 w-4 mr-1.5" />}
                        Kurs endgültig absagen
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default CancelCourseDialog;
